import { FormEvent, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const FORM_URL = 'https://readdy.ai/api/form/d76g4atr5g02gnd6a090';

export default function ApplicationSection() {
  const { ref: headRef, isVisible: headVisible } = useScrollReveal<HTMLDivElement>();
  const { ref: formRef, isVisible: formVisible } = useScrollReveal<HTMLDivElement>();
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const nameInput = form.querySelector<HTMLInputElement>('[name="name"]');
    const emailInput = form.querySelector<HTMLInputElement>('[name="email"]');
    const resumeInput = form.querySelector<HTMLTextAreaElement>('[name="background"]');
    const motivationInput = form.querySelector<HTMLTextAreaElement>('[name="motivation"]');

    if (!nameInput?.value || !emailInput?.value || !motivationInput?.value) {
      setStatus('error');
      return;
    }

    if (motivationInput.value.length > 500 || (resumeInput && resumeInput.value.length > 500)) {
      setStatus('error');
      return;
    }

    if (nameInput) data.append('name', nameInput.value);
    if (emailInput) data.append('email', emailInput.value);
    if (resumeInput) data.append('background', resumeInput.value);
    if (motivationInput) data.append('motivation', motivationInput.value);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="apply" className="bg-cream py-24 md:py-36">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        {/* Section label */}
        <div className="flex items-center gap-4 mb-16">
          <span className="font-sans text-terracotta text-[11px] tracking-[0.4em] uppercase">06</span>
          <div className="flex-1 h-px bg-warm-border" />
          <span className="font-sans text-warm-muted text-[11px] tracking-[0.3em]">Apply</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left */}
          <div
            ref={headRef}
            className={`transition-all duration-900 ${headVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <h2 className="font-serif text-charcoal text-2xl md:text-3xl lg:text-4xl leading-loose mb-8">
              応募する
            </h2>
            <div className="space-y-5 font-sans text-warm-muted text-sm md:text-base leading-loose">
              <p>
                選考は書類選考の後、オンライン面談を実施します。
                形式的な面接ではなく、お互いを知るための場として考えています。
              </p>
              <p>
                ご不明な点はフォームの「志望動機・メッセージ」欄にご記入いただくか、
                お気軽にお問い合わせください。
              </p>
            </div>

            <div className="mt-10 space-y-4">
              {[
                { step: '01', text: 'フォーム送信' },
                { step: '02', text: '書類選考（1週間以内に連絡）' },
                { step: '03', text: 'オンライン面談（30〜60分）' },
                { step: '04', text: '内定・業務委託契約締結' },
              ].map(({ step, text }) => (
                <div key={step} className="flex items-center gap-4">
                  <span className="font-sans text-terracotta text-xs tracking-widest w-6">{step}</span>
                  <div className="w-px h-4 bg-warm-border" />
                  <p className="font-sans text-charcoal text-sm">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div
            ref={formRef}
            className={`transition-all duration-1000 delay-200 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            {status === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center py-16 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-terracotta/10 rounded-full mb-6">
                  <i className="ri-check-line text-terracotta text-2xl" />
                </div>
                <h3 className="font-serif text-charcoal text-xl mb-3">ご応募ありがとうございます</h3>
                <p className="font-sans text-warm-muted text-sm leading-loose max-w-xs">
                  内容を確認のうえ、1週間以内にご連絡いたします。
                  しばらくお待ちください。
                </p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="chammi-recruit-form"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label className="block font-sans text-charcoal text-xs tracking-widest mb-2" htmlFor="name">
                    お名前 <span className="text-terracotta">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="山田 太郎"
                    className="w-full font-sans text-sm text-charcoal bg-white border border-warm-border rounded-md px-4 py-3 focus:outline-none focus:border-terracotta transition-colors placeholder:text-warm-muted/50"
                  />
                </div>

                <div>
                  <label className="block font-sans text-charcoal text-xs tracking-widest mb-2" htmlFor="email">
                    メールアドレス <span className="text-terracotta">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full font-sans text-sm text-charcoal bg-white border border-warm-border rounded-md px-4 py-3 focus:outline-none focus:border-terracotta transition-colors placeholder:text-warm-muted/50"
                  />
                </div>

                <div>
                  <label className="block font-sans text-charcoal text-xs tracking-widest mb-2" htmlFor="background">
                    経歴・スキルの概要（500字以内）
                  </label>
                  <textarea
                    id="background"
                    name="background"
                    rows={4}
                    maxLength={500}
                    placeholder="これまでの職歴、スキル、経験などを簡単にご記入ください。"
                    className="w-full font-sans text-sm text-charcoal bg-white border border-warm-border rounded-md px-4 py-3 focus:outline-none focus:border-terracotta transition-colors resize-none placeholder:text-warm-muted/50"
                  />
                </div>

                <div>
                  <label className="block font-sans text-charcoal text-xs tracking-widest mb-2" htmlFor="motivation">
                    志望動機・メッセージ <span className="text-terracotta">*</span>（500字以内）
                  </label>
                  <textarea
                    id="motivation"
                    name="motivation"
                    rows={5}
                    maxLength={500}
                    required
                    placeholder="このポジションへの応募理由や、あなた自身のことを聞かせてください。"
                    className="w-full font-sans text-sm text-charcoal bg-white border border-warm-border rounded-md px-4 py-3 focus:outline-none focus:border-terracotta transition-colors resize-none placeholder:text-warm-muted/50"
                  />
                </div>

                {status === 'error' && (
                  <p className="font-sans text-terracotta text-xs">
                    送信できませんでした。必須項目の入力と文字数をご確認ください。
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="whitespace-nowrap cursor-pointer w-full font-sans text-sm tracking-[0.2em] bg-terracotta text-white py-4 rounded-full hover:bg-terracotta-light disabled:opacity-50 transition-colors duration-300"
                >
                  {status === 'loading' ? '送信中...' : '応募する'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
